// The three-tab fixed-asset screen (batch 9 R4: COA-H1 / H3 / H4 / H6 / H7 / H8 / H12 / H14 / H15 / H16).
// Shared by its files: the URL it keeps its state in, who may write, the shapes the server sends.
// ⛔ Amounts are SATANG everywhere on this screen, both ways — display them with ui.ts `baht()`, and
// send what a person types through `toSatang()`. Never mix in a baht figure.
import { useSearchParams } from 'react-router-dom';
import type { AwCtx } from '../../ctx';

export type TabProps = { ctx: AwCtx; canWrite: boolean };
export type FaTab = 'categories' | 'register' | 'depreciation';
export const FA_TABS: FaTab[] = ['categories', 'register', 'depreciation'];

/** A read-only seat: the books login (one client, GET only) and the auditor. Writes are left OUT of the
 *  page for them, never shown disabled — middleware refuses their non-GET calls with a JSON 403, which
 *  pages the admins (CLAUDE.md memory "refused calls page admins"). */
export const isReadOnlySeat = (ctx: AwCtx) => ctx.me.role === 'books' || ctx.me.role === 'auditor';
/** Who may change anything on this screen: not a read-only seat, and not in a closed period. */
export const canWriteIn = (ctx: AwCtx) => !isReadOnlySeat(ctx) && !ctx.periodClosed;

/** "1,234.56" (or "1234.56") as typed → integer satang. Empty → 0. */
export const toSatang = (v: any): number => Math.round((parseFloat(String(v ?? '').replace(/,/g, '')) || 0) * 100);
/** integer satang → the plain "1234.56" a number input holds. */
export const toInput = (s: any): string => (Number(s) ? (Number(s) / 100).toFixed(2) : '');

/**
 * The screen's state in the URL — so a window can be linked to, survives the pane re-keying, and
 * closing it returns the list exactly as it was (COA-H16 / FS-9). ⛔ Written only through
 * useSearchParams with replace (only utils/unsaved.ts may step the browser's history), and never
 * ?menu=/?sub= — those are the shell's (ctx.setNav).
 *   fatab   the tab ('register' when absent)
 *   fa      the asset open in its window
 *   faedit  the asset open in its edit form · 'new' for the create form
 *   fasrc   an Expense record key the create form is bought on (set by the Expense record window)
 *   facat   the category open in its form (CategoriesTab) · 'new' for a new one
 * ⛔ react-router 7 builds each setSearchParams from the params of the render it was made in, so two
 * writes in a row lose the first: change several params in ONE `set({...})`, never two helpers in turn.
 */
export function useFaParams() {
  const [params, setParams] = useSearchParams();
  const set = (patch: Record<string, string>) => setParams(p => {
    const n = new URLSearchParams(p);
    for (const [k, v] of Object.entries(patch)) { if (v) n.set(k, v); else n.delete(k); }
    return n;
  }, { replace: true });
  const raw = params.get('fatab') || '';
  // ⛔ An asset window, its form, or a record to register an asset from all live on the Register tab:
  // with any of them in the URL that is the tab shown, whatever ?fatab= an earlier visit left behind —
  // otherwise the form the Expense record window asked for would open on a tab that does not draw it.
  const forced = !!(params.get('fa') || params.get('faedit') || params.get('fasrc'));
  const tab: FaTab = forced ? 'register' : (FA_TABS as string[]).includes(raw) ? raw as FaTab : 'register';
  return {
    tab, setTab: (t: FaTab) => set({ fatab: t === 'register' ? '' : t }),
    openId: params.get('fa') || '', open: (id: string) => set({ fa: id }), close: () => set({ fa: '' }),
    editId: params.get('faedit') || '', edit: (id: string) => set({ faedit: id }), endEdit: () => set({ faedit: '', fasrc: '' }),
    src: params.get('fasrc') || '',
    set,
  };
}

// ── What the server sends (services/fixedAssets.ts shapeAsset, assetDetail, DepRow; assetRecon.ts) ──
export type FaStatus = 'in_use' | 'not_yet_available' | 'idle' | 'fully_depreciated' | 'disposed' | 'written_off';
export type FaAsset = {
  id: string; asset_no: string; name_th: string; name_en: string; class_code: string; class_th: string; class_en: string;
  category_id: string; category_th: string; category_en: string; charge_nature: string;
  acquired_date: string; available_date: string; depreciation_start: string;
  cost: number; capitalised_cost: number; capitalised_total: number; salvage: number; depreciable_base: number;
  rate: number; useful_life_years: number; method: string;
  opening_accum: number; opening_as_of: string; accumulated: number; nbv: number;
  vendor_name: string; vendor_invoice_no: string; location: string; note: string;
  status: FaStatus; status_stored: string; charged: boolean; overrides: string[]; files: number;
  asset_account_id: string; accum_account_id: string; expense_account_id: string;
  disposed_date: string | null; disposal_proceeds: number | null; disposal_entry_id: string | null;
  source_kind: string; source_key: string; source_line_no: number; source_amount: number;
  created_by_name: string; created_at: number; updated_by_name: string | null; updated_at: number; edited: number;
  depreciable: number; max_rate: number | null;
};
export type FaPage = { rows: FaAsset[]; total: number; page: number; limit: number;
  summary: { count: number; cost: number; accumulated: number; nbv: number }; storedStatuses: string[]; methods: string[] };
export type FaAccountRef = { id: string; code: string; nameTh: string; nameEn: string } | null;
export type FaDetail = {
  asset: FaAsset;
  accounts: { asset: FaAccountRef; accum: FaAccountRef; expense: FaAccountRef };
  category: any | null;
  charges: { id: string; from: string; to: string; days: number; amount: number; entryId: string; docNo: string; status: string }[];
  movements: { id: string; kind: string; field: string; from: string; to: string; note: string; entryId: string; docNo: string; entryStatus: string; by: string; at: number }[];
  files: { id: string; filename: string; mime: string; size: number; by: string; at: number }[];
  disposal: { entryId: string; docNo: string; status: string } | null;
  source: { kind: string; key: string; lineNo: number; amount: number } | null;
  /** what the form may offer, and the company's frequency (read-only on the asset, decision Q6) */
  storedStatuses: string[]; methods: string[]; cadence: 'annual' | 'monthly';
};
export type FaCategory = {
  id: string; name_th: string; name_en: string; decree_class_code: string; nature: 'depreciation' | 'amortisation' | 'none';
  asset_account_id: string; accum_account_id: string; expense_account_id: string; default_method: string; default_rate: number | null;
  active: number; class_th: string; class_en: string; max_rate: number | null; depreciable: number; assets: number;
  asset_account: string; accum_account: string; expense_account: string;
  asset_account_en: string; accum_account_en: string; expense_account_en: string;
};
export type FaDepRow = {
  assetId: string; assetNo: string; name: string; from: string; to: string; days: number; cost: number; salvage: number; rate: number;
  accumBefore: number; amount: number; chargedThisPeriod: number; skipped: string;
  skippedKind: '' | 'not_depreciable' | 'no_accounts' | 'not_owned' | 'already_charged' | 'fully_depreciated' | 'not_available';
  chargeNature: string; categoryId: string; categoryTh: string; categoryEn: string; usefulLifeYears: number; method: string;
  capitalisedTotal: number; openingAccum: number; periodCharge: number; closingAccum: number; closingNbv: number; disposedDate: string;
};
export type FaRun = { entryId: string; docNo: string; status: string; date: string; windowFrom: string; windowTo: string; amount: number; assets: number;
  reversedBy: { entryId: string; docNo: string; status: string } | null;
  /** Why reversing it would be refused (services/fixedAssets.ts runReversalRefusal) — a code and the
   *  numbers it names, worded on screen; null when it may be reversed, or is not a posted run. */
  refusal: { code: string; docNo: string; assetNo: string; from: string; to: string } | null };
export type FaReconAccount = { accountId: string; code: string; nameTh: string; nameEn: string; register: number; gl: number; diff: number };
export type FaReconReason = { code: string; assets: number; amount: number; assetNos: string[] };
export type FaRecon = { asOf: string; periodId: string;
  cost: { accounts: FaReconAccount[]; register: number; gl: number; diff: number };
  accum: { accounts: FaReconAccount[]; register: number; gl: number; diff: number };
  nbv: { register: number; gl: number; diff: number };
  reasons: FaReconReason[]; notes: FaReconReason[]; unexplained: number; balanced: boolean };
export type FaSourceLine = { lineNo: number; description: string; accountId: string; accountCode: string; accountName: string;
  isAssetAccount: boolean; amount: number; registered: number; remaining: number; why: string };
export type FaSource = { record: { key: string; expenseNo: string; supplier: string; supplierTaxId: string; supplierNo: string; date: string; currency: string; lineId: string } | null;
  lines: FaSourceLine[]; refusal: { code: string; why: string } | null };

/** The i18n key of each status (mt/facommon.ts). */
export const STATUS_KEY: Record<FaStatus, string> = {
  in_use: 'aw_fa_status_in_use', not_yet_available: 'aw_fa_status_not_yet_available', idle: 'aw_fa_status_idle',
  fully_depreciated: 'aw_fa_status_fully_depreciated', disposed: 'aw_fa_status_disposed', written_off: 'aw_fa_status_written_off',
};
